import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, FC, ReactNode, RefObject } from 'react';
import { Input } from 'antd';

import DownSvg from 'assets/svg/Down';
import { colorConstants } from 'config/colorConstants';

import {
    BaseActionButtonOutlineShadow,
    BaseControlTypography,
    BaseDropdownMenuShadow,
    getOptionLabelText,
    renderWithOptionalTooltip,
    resolveTooltipText,
    useOutsideDismiss,
    type BaseDropdownControlProps,
    type GenericDropdownControlProps,
    type GenericFormControlOption,
    type GenericSearchableDropdownControlProps,
    type OptionValue,
} from './formControl';

type DropdownOptionsMenuProps = {
    options: GenericFormControlOption[];
    onSelect: (value: OptionValue, isDisabled?: boolean) => void;
};

const DropdownOptionsMenu = ({ options, onSelect }: DropdownOptionsMenuProps) => (
    <div
        className="absolute left-0 top-[calc(100%+4px)] z-50 w-full rounded-lg p-1"
        style={{ boxShadow: BaseDropdownMenuShadow, background: colorConstants.white }}
    >
        <div className="max-h-52 overflow-auto">
            {options.length ? (
                options.map((option) => (
                    <button
                        key={String(option.value)}
                        type="button"
                        disabled={option.disabled}
                        className={`flex h-8 w-full items-center rounded px-3 py-[5px] text-left ${
                            option.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                        }`}
                        style={{
                            ...BaseControlTypography,
                            color: option.disabled ? colorConstants.greyAlt12 : colorConstants.blackTransparent88,
                        }}
                        onMouseEnter={(event) => {
                            if (option.disabled) return;
                            event.currentTarget.style.background = colorConstants.blackTransparent04;
                        }}
                        onMouseLeave={(event) => {
                            event.currentTarget.style.background = 'transparent';
                        }}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => onSelect(option.value, option.disabled)}
                    >
                        <span className="truncate">{option.label}</span>
                    </button>
                ))
            ) : (
                <div className="px-3 py-2 text-[12px]" style={{ color: colorConstants.greyAlt14 }}>
                    No matching option
                </div>
            )}
        </div>
    </div>
);

type DropdownSearchTriggerProps = {
    searchInputRef: RefObject<any>;
    searchKey: string;
    disabled?: boolean;
    placeholder: string;
    labelPrefix?: string;
    triggerShellStyle: CSSProperties;
    triggerTextColor: string;
    onSearchChange: (value: string) => void;
    onEscape: () => void;
};

const DropdownSearchTrigger = ({
    searchInputRef,
    searchKey,
    disabled,
    placeholder,
    labelPrefix,
    triggerShellStyle,
    triggerTextColor,
    onSearchChange,
    onEscape,
}: DropdownSearchTriggerProps) => (
    <div className="flex h-8 w-full items-center justify-between rounded-md border px-3" style={triggerShellStyle}>
        <div className="flex min-w-0 flex-1 items-center">
            {labelPrefix ? (
                <span className="shrink-0" style={{ color: colorConstants.toolbarLabelGrey }}>
                    {labelPrefix}:
                </span>
            ) : null}
            <Input
                ref={searchInputRef}
                value={searchKey}
                disabled={disabled}
                placeholder={placeholder}
                bordered={false}
                className={`!h-full !min-w-0 !flex-1 !border-0 !bg-transparent !px-0 !outline-none !shadow-none hover:!border-0 hover:!shadow-none focus:!border-0 focus:!outline-none focus:!shadow-none [&.ant-input-focused]:!border-0 [&.ant-input-focused]:!shadow-none ${
                    labelPrefix ? '!ml-[2px]' : ''
                }`}
                style={{
                    ...BaseControlTypography,
                    color: triggerTextColor,
                    border: 'none',
                    borderWidth: 0,
                    boxShadow: 'none',
                    outline: 'none',
                    background: 'transparent',
                }}
                onChange={(event) => onSearchChange(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        onEscape();
                    }
                }}
            />
        </div>
        <span className="ml-2 flex size-4 shrink-0 items-center justify-center">
            <span className="rotate-180 transition-transform">
                <DownSvg dropDownStroke={colorConstants.toolbarLabelGrey} />
            </span>
        </span>
    </div>
);

const getDropdownTriggerTextColor = (disabled: boolean, styleColor?: string | number) => {
    if (disabled) return colorConstants.greyAlt12;
    return String(styleColor ?? colorConstants.purpleFontPressed);
};

const getDropdownTriggerShellStyle = (
    isOpen: boolean,
    disabled: boolean,
    triggerTextColor: string,
): CSSProperties => ({
    borderColor: isOpen ? colorConstants.purpleFontPressed : colorConstants.toolbarBorderGrey,
    background: disabled ? colorConstants.radioLabelHoverBg : colorConstants.white,
    color: triggerTextColor,
    boxShadow: BaseActionButtonOutlineShadow,
    ...BaseControlTypography,
});

const getFilteredDropdownOptions = (
    options: GenericFormControlOption[],
    searchable: boolean,
    searchKey: string,
) => {
    if (!searchable) return options;
    const query = searchKey.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) => getOptionLabelText(option.label).toLowerCase().includes(query));
};

type DropdownClosedTriggerProps = {
    disabled: boolean;
    isOpen: boolean;
    labelPrefix?: string;
    selectedLabel?: ReactNode;
    placeholder: string;
    triggerShellStyle: CSSProperties;
    onToggle: () => void;
};

const DropdownClosedTrigger = ({
    disabled,
    isOpen,
    labelPrefix,
    selectedLabel,
    placeholder,
    triggerShellStyle,
    onToggle,
}: DropdownClosedTriggerProps) => (
    <button
        type="button"
        disabled={disabled}
        className={`flex h-8 w-full items-center justify-between rounded-md border px-3 text-[14px] leading-[22px] ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
        style={triggerShellStyle}
        onClick={onToggle}
    >
        <span className="flex min-w-0 items-center">
            {labelPrefix ? <span style={{ color: colorConstants.toolbarLabelGrey }}>{labelPrefix}:</span> : null}
            <span className={`truncate ${labelPrefix ? 'ml-[2px]' : ''}`}>
                {selectedLabel ?? <span style={{ color: colorConstants.greyAlt12 }}>{placeholder}</span>}
            </span>
        </span>
        <span className="ml-2 flex size-4 shrink-0 items-center justify-center">
            <span className={`transition-transform ${isOpen ? 'rotate-180' : ''}`.trim()}>
                <DownSvg dropDownStroke={colorConstants.toolbarLabelGrey} />
            </span>
        </span>
    </button>
);

const getDropdownLabels = (
    placeholder: string | undefined,
    selectedLabelText: string,
    searchPlaceholder: string,
) => {
    const resolvedPlaceholder = placeholder ?? 'Select';
    return {
        resolvedPlaceholder,
        tooltipFallback: selectedLabelText || resolvedPlaceholder,
        searchInputPlaceholder: selectedLabelText || resolvedPlaceholder || searchPlaceholder,
    };
};

const getDropdownContainerClassName = (className: string | undefined, isOpen: boolean) =>
    `relative w-full ${className ?? ''} ${isOpen ? 'is-open' : ''}`.trim();

const shouldShowDropdownTooltip = (showTooltip: boolean, isOpen: boolean) => showTooltip && !isOpen;

const BaseDropdownControl = ({
    options,
    value,
    onChange,
    placeholder,
    disabled = false,
    className,
    style,
    showTooltip = false,
    tooltipText,
    staticTooltipText,
    searchable = false,
    labelPrefix,
    searchPlaceholder = 'Search',
}: BaseDropdownControlProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const searchInputRef = useRef<any>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [searchKey, setSearchKey] = useState('');

    const selectedOption = useMemo(() => options.find((option) => option.value === value), [options, value]);
    const selectedLabelText = getOptionLabelText(selectedOption?.label ?? '');
    const { resolvedPlaceholder, tooltipFallback, searchInputPlaceholder } = getDropdownLabels(
        placeholder,
        selectedLabelText,
        searchPlaceholder,
    );
    const resolvedTooltipText = resolveTooltipText(staticTooltipText, tooltipText, tooltipFallback);
    const triggerTextColor = getDropdownTriggerTextColor(disabled, style?.color);

    const filteredOptions = useMemo(
        () => getFilteredDropdownOptions(options, searchable, searchKey),
        [options, searchable, searchKey],
    );

    const closeDropdown = useCallback(() => {
        setIsOpen(false);
        setSearchKey('');
    }, []);

    const openDropdown = useCallback(() => {
        if (disabled) return;
        setIsOpen(true);
        setSearchKey('');
    }, [disabled]);

    const handleClosedTriggerToggle = useCallback(() => {
        if (disabled) return;
        if (searchable) {
            openDropdown();
            return;
        }
        setIsOpen((previous) => !previous);
    }, [disabled, openDropdown, searchable]);

    useOutsideDismiss(containerRef, isOpen, closeDropdown);

    useEffect(() => {
        if (!isOpen || !searchable) return undefined;
        const frameId = window.requestAnimationFrame(() => {
            searchInputRef.current?.focus?.({ preventScroll: true });
            searchInputRef.current?.select?.();
        });
        return () => window.cancelAnimationFrame(frameId);
    }, [isOpen, searchable]);

    const handleOptionSelect = (nextValue: OptionValue, isDisabled?: boolean) => {
        if (isDisabled) return;
        onChange(nextValue);
        closeDropdown();
    };

    const triggerShellStyle = getDropdownTriggerShellStyle(isOpen, disabled, triggerTextColor);
    const showSearchTrigger = searchable && isOpen;

    const control = (
        <div
            ref={containerRef}
            className={getDropdownContainerClassName(className, isOpen)}
            style={style}
        >
            {showSearchTrigger ? (
                <DropdownSearchTrigger
                    searchInputRef={searchInputRef}
                    searchKey={searchKey}
                    disabled={disabled}
                    placeholder={searchInputPlaceholder}
                    labelPrefix={labelPrefix}
                    triggerShellStyle={triggerShellStyle}
                    triggerTextColor={triggerTextColor}
                    onSearchChange={setSearchKey}
                    onEscape={closeDropdown}
                />
            ) : (
                <DropdownClosedTrigger
                    disabled={disabled}
                    isOpen={isOpen}
                    labelPrefix={labelPrefix}
                    selectedLabel={selectedOption?.label}
                    placeholder={resolvedPlaceholder}
                    triggerShellStyle={triggerShellStyle}
                    onToggle={handleClosedTriggerToggle}
                />
            )}

            {isOpen ? <DropdownOptionsMenu options={filteredOptions} onSelect={handleOptionSelect} /> : null}
        </div>
    );

    return renderWithOptionalTooltip(control, shouldShowDropdownTooltip(showTooltip, isOpen), resolvedTooltipText);
};

export const GenericDropdownControl: FC<GenericDropdownControlProps> = (props) => (
    <BaseDropdownControl {...props} searchable={false} />
);

export const GenericSearchableDropdownControl: FC<GenericSearchableDropdownControlProps> = (props) => (
    <BaseDropdownControl {...props} searchable searchPlaceholder="Search" />
);

export default GenericDropdownControl;
